import { UseQueryResult } from '@tanstack/react-query'
import dayjs, { Dayjs } from 'dayjs'
import React from 'react'
import { GetCurrentTaxYearQuery, GetRolesFromUserQuery, useGetRolesFromUserQuery } from '../generated/graphql'
import useAuth from './useAuth'
import useGetCurrentTaxYear from './useGetCurrentTaxYear'
import { Permissions as UserPermissions } from '@/components/ProtectedComponent/permissionName'

import { isByPassDisabled } from '@/config'

interface GlobalContextProviderProps {
  children: React.ReactNode
}

interface GlobalContextType {
  currentTaxYear: number
  currentTaxYearMinDate: Dayjs
  currentTaxYearMaxDate: Dayjs
  getCurrentTaxYearQueryResponse: UseQueryResult<GetCurrentTaxYearQuery, unknown>
  getRolesFromUserQuery: UseQueryResult<GetRolesFromUserQuery, unknown>
  hasPermission: (permission?: string | string[] | UserPermissions) => boolean
}

const GlobalContext = React.createContext<GlobalContextType>({} as GlobalContextType)

export function GlobalContextProvider({ children }: GlobalContextProviderProps): JSX.Element {
  const [currentTaxYear, setCurrentTaxYear] = React.useState<number>(new Date().getFullYear())
  const { authInformation } = useAuth()

  const currentTaxYearMaxDate = React.useMemo(() => {
    return getMaxDateForYear(currentTaxYear)
  }, [currentTaxYear])

  const currentTaxYearMinDate = React.useMemo(() => {
    return getMinDateForYear(currentTaxYear)
  }, [currentTaxYear])

  const getCurrentTaxYearQueryResponse = useGetCurrentTaxYear({ setCurrentTaxYear })
  const getRolesFromUserQuery = useGetRolesFromUserQuery(
    { userId: Number(authInformation?.userId) },
    {
      enabled: !!authInformation && !authInformation?.isMasterUser && !authInformation?.isMcatUser,
      gcTime: 60,
      refetchInterval: 0,
      refetchIntervalInBackground: true,
      retry: 5,
    },
  )

  const hasPermission = React.useCallback(
    (permissions?: string | string[] | UserPermissions): boolean => {
      if (!isByPassDisabled) return true
      if (!permissions) return true
      if (authInformation?.isMasterUser || authInformation?.isMcatUser) return true
      const permissionList = Array.isArray(permissions) ? permissions : [permissions]
      return permissionList.every((permission) =>
        getRolesFromUserQuery.data?.getRolesFromUser?.some((role) =>
          role.permissions.some((p) => p.name === permission),
        ),
      )
    },
    [authInformation, getRolesFromUserQuery.data?.getRolesFromUser],
  )

  const authContext = {
    currentTaxYear,
    currentTaxYearMaxDate,
    currentTaxYearMinDate,
    getCurrentTaxYearQueryResponse,
    getRolesFromUserQuery,
    hasPermission,
  }

  return <GlobalContext.Provider value={authContext}>{children}</GlobalContext.Provider>
}

function getMaxDateForYear(year: number = new Date().getFullYear()): Dayjs {
  return dayjs.utc(`${year}-12-31T23:59:59.999Z`)
}

function getMinDateForYear(year: number = new Date().getFullYear()): Dayjs {
  return dayjs.utc(`${year}-01-01T00:00:00.000Z`)
}

export default function useGlobalContext(): GlobalContextType {
  return React.useContext(GlobalContext)
}
